/**
 * useCountdownAnchor — web hook that fetches the director-controlled
 * countdown anchor `CalendarEvent` for a given competition year via
 * the `getCountdownAnchor(competitionYear: Int!)` GraphQL query.
 *
 * The query is registered in the backend with both `@aws_api_key` and
 * `@aws_cognito_user_pools` directives (R4.3 in
 * `.kiro/specs/countdown-anchor/design.md`). On web we use
 * `authMode: 'apiKey'` here so the countdown banner can render before
 * Cognito hydration completes (e.g., on the public landing surface or
 * the first paint of the dashboard while auth is resolving). This
 * matches the public-readable pattern that `listCalendarEvents`
 * already uses in `CalendarPage.tsx`.
 *
 * Returned shape (kept identical to the mobile hook so the two
 * platforms share consumer logic):
 *   - `anchor`   — the resolved `CalendarEvent` or `null` when no
 *                  anchor is set for the year (the resolver returns
 *                  `null`, NOT an error, in that case — R4.4).
 *   - `loading`  — `true` until the first fetch resolves.
 *   - `error`    — last network/GraphQL error or `null`. Banner
 *                  consumers use this together with a cached
 *                  fallback to avoid flicker.
 *   - `refetch`  — re-runs the query and resets loading/error.
 *
 * Concurrency guards:
 *   - `mountedRef` prevents `setState` after unmount (avoids the
 *     "state update on unmounted component" warning if the component
 *     is unmounted mid-flight).
 *   - `fetchIdRef` is bumped on every refetch so a slow earlier
 *     request cannot overwrite a faster later request's result
 *     (last-write-wins ordering bug).
 *
 * Timeout guard:
 *   - The in-flight `getCountdownAnchor` call is raced against a
 *     10-second timer. A request still pending after the timeout is
 *     surfaced as an `error` (with `loading` flipped to `false`) so a
 *     consumer can apply its failure fallback rather than remaining in
 *     flight indefinitely. The timer is always cleared once the race
 *     settles, and the existing `mountedRef`/`fetchIdRef` ordering
 *     still drops a late response (public-site-countdown-banner R2.8).
 *
 * Requirements: 4.1, 4.3, 8.4 (countdown-anchor); 2.8
 *   (public-site-countdown-banner)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { getCountdownAnchor as GET_COUNTDOWN_ANCHOR } from '@/graphql/queries';

/**
 * Subset of the backend `CalendarEvent` GraphQL type that the
 * countdown surface needs. Mirrors the interface defined in the
 * mobile hook (`mobile/app/src/hooks/useCountdownAnchor.ts`) so the
 * two hooks have a structurally identical return type.
 *
 * Field semantics (per `infrastructure/graphql/schema.graphql`):
 *   - `date`              — ISO-8601 date in `YYYY-MM-DD` form.
 *   - `time`              — Display string ("7:00 PM" or "19:00") or
 *                           empty string when `allDay` is true.
 *   - `allDay`            — When true, `time` is ignored; the
 *                           countdown anchors to 00:00 local.
 *   - `competitionYear`   — Integer year (e.g., 2026).
 *   - `isCountdownAnchor` — `true` for the (at most one) row marked
 *                           as the year's anchor.
 */
export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  allDay: boolean;
  competitionYear: number;
  isCountdownAnchor: boolean;
}

/** Shape returned by the hook. */
export interface UseCountdownAnchorResult {
  anchor: CalendarEvent | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface GetCountdownAnchorResult {
  getCountdownAnchor: CalendarEvent | null;
}

// Module-level client. `generateClient()` is safe to call once at
// import time — it's a thin handle around the configured Amplify
// API category and matches how the other public-readable hooks in
// this folder construct their client (e.g., `useNotificationCenter`,
// `useDMCandidates`).
const client = generateClient();

/**
 * Maximum time (ms) the hook waits for a `getCountdownAnchor` response
 * before treating the request as failed. A request still pending past
 * this window resolves the internal race with a timeout error so the
 * banner can apply its failure fallback instead of staying in flight
 * indefinitely (public-site-countdown-banner R2.8).
 */
const REQUEST_TIMEOUT_MS = 10_000;

/** Sentinel used to distinguish the timeout branch of the race. */
const TIMEOUT = Symbol('useCountdownAnchor:timeout');

export function useCountdownAnchor(
  competitionYear: number,
): UseCountdownAnchorResult {
  const [anchor, setAnchor] = useState<CalendarEvent | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Tracks whether the hook is still mounted; flipped to false in
  // the cleanup function below.
  const mountedRef = useRef<boolean>(true);

  // Bumped on each refetch so a stale earlier response can't clobber
  // a fresher later one when they resolve out of order.
  const fetchIdRef = useRef<number>(0);

  const runFetch = useCallback(async () => {
    const id = ++fetchIdRef.current;

    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      // Race the in-flight request against a 10s timer. A timer handle
      // is kept so the success/error path can always clear it and
      // avoid leaking a pending timeout (public-site-countdown-banner
      // R2.8). The timer resolves with a TIMEOUT sentinel rather than
      // rejecting so the timeout is handled inline below.
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<typeof TIMEOUT>((resolve) => {
        timeoutHandle = setTimeout(() => resolve(TIMEOUT), REQUEST_TIMEOUT_MS);
      });

      const request = client.graphql({
        query: GET_COUNTDOWN_ANCHOR,
        variables: { competitionYear },
        authMode: 'apiKey',
      }) as Promise<GraphQLResult<GetCountdownAnchorResult>>;

      let raced: GraphQLResult<GetCountdownAnchorResult> | typeof TIMEOUT;
      try {
        raced = await Promise.race([request, timeout]);
      } finally {
        // Always clear the timer so it cannot fire after the race
        // settles (success, error, or timeout).
        if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      }

      // Stale-fetch guard: ignore if a newer fetch superseded us or
      // the component unmounted mid-flight.
      if (!mountedRef.current || id !== fetchIdRef.current) return;

      // Pending beyond the timeout window: surface as an error with
      // loading cleared. The original request promise is intentionally
      // left to settle on its own; the fetchId/mounted guards above
      // drop its late result.
      if (raced === TIMEOUT) {
        setError(new Error('getCountdownAnchor request timed out'));
        setLoading(false);
        return;
      }

      setAnchor(raced.data?.getCountdownAnchor ?? null);
      setLoading(false);
    } catch (err) {
      if (!mountedRef.current || id !== fetchIdRef.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
  }, [competitionYear]);

  // Stable identity so consumers can safely include `refetch` in
  // dependency arrays without retriggering effects.
  const refetch = useCallback(() => {
    void runFetch();
  }, [runFetch]);

  useEffect(() => {
    mountedRef.current = true;
    void runFetch();
    return () => {
      mountedRef.current = false;
    };
  }, [runFetch]);

  return { anchor, loading, error, refetch };
}
